import React, { useState } from 'react';
import { 
  Search, Filter, Plus, Tag, Trash2, Edit, Eye, Download, 
  FileText, Calendar, User, Clock, ChevronDown, ChevronUp, 
  X, Maximize2, Minimize2, ExternalLink, ArrowLeft, ArrowRight,
  SlidersHorizontal, Grid, List, Copy, Star, Presentation,
  DollarSign
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';

interface SlideDeckTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  thumbnailUrl: string;
  slideCount: number;
  createdAt: string;
  updatedAt: string;
  author: string;
  previewUrl: string;
  featured: boolean;
}

const SlideDeckTemplates: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'popularity'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [lastClickedItem, setLastClickedItem] = useState<string | null>(null);
  
  // Sample data for slide deck templates
  const templates: SlideDeckTemplate[] = [
    {
      id: 'tax-return-qualification',
      name: 'Tax Return Qualification Seminar',
      description: 'Presentation for tax return qualification seminar targeting adults 50+ with $100k+ in savings.',
      category: 'Tax Planning',
      tags: ['Tax Return', 'Seminar', 'Retirement', 'Lead Generation'],
      thumbnailUrl: 'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg',
      slideCount: 7,
      createdAt: '2025-06-15T10:30:00Z',
      updatedAt: '2025-06-15T10:30:00Z',
      author: 'Tax Strategy Team',
      previewUrl: '/dashboard/slide-decks/tax-return-qualification',
      featured: true
    },
    {
      id: 'retirement-planning',
      name: 'Retirement Planning Essentials',
      description: 'A comprehensive slide deck covering all aspects of retirement planning for clients nearing retirement.',
      category: 'Retirement',
      tags: ['Retirement', 'Financial Planning', 'Income Strategies'],
      thumbnailUrl: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg',
      slideCount: 24,
      createdAt: '2025-05-15T10:30:00Z',
      updatedAt: '2025-06-01T14:45:00Z',
      author: 'Financial Education Team',
      previewUrl: '/dashboard/slide-decks/retirement-planning',
      featured: true
    },
    {
      id: 'tax-strategies',
      name: 'Tax-Efficient Investment Strategies',
      description: 'Slide deck explaining tax-efficient investment strategies for high-net-worth clients.',
      category: 'Tax Planning',
      tags: ['Tax Planning', 'Investments', 'High Net Worth'],
      thumbnailUrl: 'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg',
      slideCount: 18,
      createdAt: '2025-04-20T09:15:00Z',
      updatedAt: '2025-05-25T11:30:00Z',
      author: 'Tax Strategy Team',
      previewUrl: '/dashboard/slide-decks/tax-strategies',
      featured: false
    },
    {
      id: 'estate-planning',
      name: 'Estate Planning Fundamentals',
      description: 'A comprehensive overview of estate planning concepts and strategies for clients.',
      category: 'Estate Planning',
      tags: ['Estate Planning', 'Trusts', 'Legacy'],
      thumbnailUrl: 'https://images.pexels.com/photos/7688159/pexels-photo-7688159.jpeg',
      slideCount: 32,
      createdAt: '2025-03-10T14:20:00Z',
      updatedAt: '2025-06-05T16:45:00Z',
      author: 'Estate Planning Team',
      previewUrl: '/dashboard/slide-decks/estate-planning',
      featured: true
    },
    {
      id: 'investment-basics',
      name: 'Investment Fundamentals',
      description: 'An educational slide deck covering investment basics for new clients.',
      category: 'Investments',
      tags: ['Investments', 'Education', 'Fundamentals'],
      thumbnailUrl: 'https://images.pexels.com/photos/8867482/pexels-photo-8867482.jpeg',
      slideCount: 20,
      createdAt: '2025-02-15T11:45:00Z',
      updatedAt: '2025-05-20T13:30:00Z',
      author: 'Investment Education Team',
      previewUrl: '/dashboard/slide-decks/investment-basics',
      featured: false
    },
    {
      id: 'social-security',
      name: 'Social Security Optimization',
      description: 'Strategies for optimizing Social Security benefits for maximum retirement income.',
      category: 'Retirement',
      tags: ['Retirement', 'Social Security', 'Income Planning'],
      thumbnailUrl: 'https://images.pexels.com/photos/8867431/pexels-photo-8867431.jpeg',
      slideCount: 16,
      createdAt: '2025-01-25T10:00:00Z',
      updatedAt: '2025-04-15T09:30:00Z',
      author: 'Retirement Planning Team',
      previewUrl: '/dashboard/slide-decks/social-security',
      featured: false
    },
    {
      id: 'insurance-planning',
      name: 'Insurance Planning Strategies',
      description: 'A comprehensive overview of insurance planning strategies for risk management.',
      category: 'Insurance',
      tags: ['Insurance', 'Risk Management', 'Protection'],
      thumbnailUrl: 'https://images.pexels.com/photos/8867495/pexels-photo-8867495.jpeg',
      slideCount: 22,
      createdAt: '2025-03-05T13:15:00Z',
      updatedAt: '2025-05-10T15:20:00Z',
      author: 'Insurance Planning Team',
      previewUrl: '/dashboard/slide-decks/insurance-planning',
      featured: true
    },
    {
      id: 'college-funding',
      name: 'College Funding Strategies',
      description: 'Educational slide deck covering college funding options and strategies.',
      category: 'Education Planning',
      tags: ['Education', 'College Funding', 'Financial Planning'],
      thumbnailUrl: 'https://images.pexels.com/photos/7688386/pexels-photo-7688386.jpeg',
      slideCount: 18,
      createdAt: '2025-02-20T14:30:00Z',
      updatedAt: '2025-04-25T10:45:00Z',
      author: 'Education Planning Team',
      previewUrl: '/dashboard/slide-decks/college-funding',
      featured: false
    },
    {
      id: 'market-outlook',
      name: 'Market Outlook & Economic Forecast',
      description: 'Current market analysis and economic forecasts for client presentations.',
      category: 'Market Analysis',
      tags: ['Market Analysis', 'Economic Forecast', 'Investments'],
      thumbnailUrl: 'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg',
      slideCount: 28,
      createdAt: '2025-05-01T09:45:00Z',
      updatedAt: '2025-06-10T11:15:00Z',
      author: 'Investment Research Team',
      previewUrl: '/dashboard/slide-decks/market-outlook',
      featured: true
    }
  ];

  // Get all unique categories
  const categories = ['all', ...new Set(templates.map(template => template.category))];
  
  // Get all unique tags
  const allTags = [...new Set(templates.flatMap(template => template.tags))];

  // Toggle tag selection
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };

  // Filter templates based on search, category, and tags
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchTerm === '' || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.every(tag => template.tags.includes(tag));
    
    return matchesSearch && matchesCategory && matchesTags;
  });

  // Sort templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (sortBy === 'name') {
      return sortOrder === 'asc' 
        ? a.name.localeCompare(b.name) 
        : b.name.localeCompare(a.name);
    } else if (sortBy === 'date') {
      return sortOrder === 'asc' 
        ? new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime() 
        : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    } else { // popularity - using featured status as a proxy
      if (a.featured && !b.featured) return sortOrder === 'asc' ? 1 : -1;
      if (!a.featured && b.featured) return sortOrder === 'asc' ? -1 : 1;
      return 0;
    }
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleTemplateClick = (template: SlideDeckTemplate) => {
    // Open the template in a new window/tab
    window.open(template.previewUrl, '_blank');
    
    // Highlight the clicked item briefly for visual feedback
    setLastClickedItem(template.id);
    setTimeout(() => {
      setLastClickedItem(null);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Slide Deck Templates</h2>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="flex items-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {showFilters ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>
          <Button className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                title="Grid view"
              >
                <Grid className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                title="List view"
              >
                <List className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'popularity')}
                className="border rounded-md py-2 px-3 text-sm focus:ring-primary focus:border-primary"
              >
                <option value="name">Name</option>
                <option value="date">Date Updated</option>
                <option value="popularity">Popularity</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 rounded-md hover:bg-gray-100"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-gray-50 min-h-[42px]">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedTags.includes(tag)
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                    >
                      {tag}
                      {selectedTags.includes(tag) && (
                        <X 
                          className="h-3 w-3 ml-1" 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTag(tag);
                          }} 
                        />
                      )}
                    </button>
                  ))}
                  {allTags.length === 0 && (
                    <span className="text-sm text-gray-500">No tags available</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Templates Grid/List View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedTemplates.map((template) => (
            <div
              key={template.id}
              className={`bg-white rounded-lg shadow-sm border hover:shadow-lg transition-all duration-300 ${
                lastClickedItem === template.id 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-200 hover:border-primary'
              } cursor-pointer overflow-hidden flex flex-col`}
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={template.thumbnailUrl}
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
                {template.featured && (
                  <div className="absolute top-2 right-2 bg-primary text-white text-xs font-semibold py-1 px-2 rounded-full">
                    Featured
                  </div>
                )}
                <div className="absolute bottom-2 right-2 bg-white/90 text-xs font-medium py-1 px-2 rounded-full">
                  {template.slideCount} slides
                </div>
              </div>
              
              <div className="p-4 flex-grow">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.name}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{template.description}</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {template.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-800">
                      +{template.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>{formatDate(template.updatedAt)}</span>
                </div>
                <div className="flex items-center">
                  <User className="h-3 w-3 mr-1" />
                  <span>{template.author}</span>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex gap-2">
                <a
                  href={template.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center gap-1 text-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTemplateClick(template);
                  }}
                >
                  <Eye className="w-3 h-3" />
                  Preview
                </a>
                <a
                  href={template.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-1 text-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTemplateClick(template);
                  }}
                >
                  <Copy className="w-3 h-3" />
                  Use Template
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slides
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Author
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTemplates.map((template) => (
                <tr 
                  key={template.id}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    lastClickedItem === template.id ? 'bg-green-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-16 rounded overflow-hidden">
                        <img 
                          src={template.thumbnailUrl} 
                          alt={template.name}
                          className="h-full w-full object-cover" 
                        />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{template.name}</div>
                          {template.featured && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary text-white">
                              Featured
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 line-clamp-1">{template.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {template.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.slideCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(template.updatedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.author}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <a 
                        href={template.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-dark"
                        title="Preview"
                        onClick={(e) => {
                          e.preventDefault();
                          handleTemplateClick(template);
                        }}
                      >
                        <Eye className="h-5 w-5" />
                      </a>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Edit functionality would go here
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Duplicate functionality would go here
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        title="Duplicate"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Delete functionality would go here
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {sortedTemplates.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
          <Presentation className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No slide deck templates found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedCategory !== 'all' || selectedTags.length > 0
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Create your first slide deck template to get started.'}
          </p>
          <Button className="mx-auto">
            <Plus className="h-4 w-4 mr-2" />
            Create New Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default SlideDeckTemplates;